import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SearchBarProps } from '../types';
import {
  SearchBarContainer,
  SearchInputWrapper,
  InlineControls,
  AddEventButton,
  ClearSearchButton,
  MobileCloseSearchButton,
} from '../styles/SearchBar.styles';
import { bucketQueryLength } from '../analytics/contract';
import { trackProductEvent } from '../analytics/productAnalytics';

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, inlineControls }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const getQueryTerm = () => new URLSearchParams(location.search).get('q') || '';
  const [term, setTerm] = useState(getQueryTerm);
  const [mobileOpen, setMobileOpen] = useState(Boolean(getQueryTerm()));
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentTerm = useRef(getQueryTerm());
  const pendingTerm = useRef<string | null>(null);
  const liveLocation = useRef(location);
  const ownQueryCommit = useRef<{ pathname: string; queryTerm: string; sourceKey: string } | null>(null);
  const previousLocation = useRef<{ pathname: string; queryTerm: string } | null>(null);
  liveLocation.current = location;

  // Hide Add Show button on settings page
  const hideAddEventButton = location.pathname === '/settings';

  const cancelPendingSearch = () => {
    if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    debounceTimer.current = null;
    pendingTerm.current = null;
  };

  // Apply q to the latest location so sibling filter changes survive the debounce.
  const updateSearchUrl = (value: string) => {
    const currentLocation = liveLocation.current;
    const params = new URLSearchParams(currentLocation.search);
    const cleanValue = value.trim();

    if (cleanValue) {
      params.set('q', cleanValue);
    } else {
      params.delete('q');
    }

    const search = params.toString();
    ownQueryCommit.current = {
      pathname: currentLocation.pathname,
      queryTerm: cleanValue,
      sourceKey: currentLocation.key,
    };
    navigate(
      {
        pathname: currentLocation.pathname,
        search: search ? `?${search}` : '',
        hash: currentLocation.hash,
      },
      { replace: true }
    );
  };

  const commitSearch = (value: string) => {
    onSearch(value.toLowerCase());
    updateSearchUrl(value);
    trackProductEvent('discovery_search_performed', {
      surface: 'search_bar',
      query_length_bucket: bucketQueryLength(value),
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    cancelPendingSearch();
    setTerm(value);
    currentTerm.current = value;
    pendingTerm.current = value;

    // Debounce search to avoid re-filtering on every keystroke
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      pendingTerm.current = null;
      commitSearch(value);
    }, 300);
  };

  const handleClear = () => {
    cancelPendingSearch();
    setTerm('');
    currentTerm.current = '';
    commitSearch('');
  };

  useEffect(() => {
    const queryTerm = getQueryTerm();
    const previous = previousLocation.current;
    const queryChanged = previous === null || queryTerm !== previous.queryTerm;
    const pathnameChanged = previous !== null && location.pathname !== previous.pathname;
    previousLocation.current = { pathname: location.pathname, queryTerm };
    const ownCommit = ownQueryCommit.current;
    const isOwnCommit = ownCommit !== null && location.key !== ownCommit.sourceKey &&
      location.pathname === ownCommit.pathname && queryTerm === ownCommit.queryTerm;
    // Consume even same-q commits, so later history visits are external changes.
    if (ownCommit !== null && location.key !== ownCommit.sourceKey) {
      ownQueryCommit.current = null;
    }

    if (queryChanged && !isOwnCommit) {
      const hadPendingSearch = pendingTerm.current !== null;
      cancelPendingSearch();
      // Own commits can arrive after another keystroke under startTransition.
      // Only external q changes may cancel or replace that newer draft.
      if (queryTerm !== currentTerm.current.trim()) {
        setTerm(queryTerm);
        currentTerm.current = queryTerm;
        onSearch(queryTerm.toLowerCase());
      } else if (previous === null || hadPendingSearch) {
        onSearch(queryTerm.toLowerCase());
      }
    } else if (pathnameChanged && pendingTerm.current !== null) {
      // A state selector changes the path but retains q. Cancel the old timer
      // and commit the draft against the new path, keeping results in sync.
      const pending = pendingTerm.current;
      cancelPendingSearch();
      commitSearch(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.pathname, location.search, onSearch]);

  useEffect(() => {
    const toggleMobileSearch = () => setMobileOpen((current) => !current);
    window.addEventListener('tcg-calendar-toggle-search', toggleMobileSearch);
    return () => window.removeEventListener('tcg-calendar-toggle-search', toggleMobileSearch);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    };
  }, []);

  return (
    <SearchBarContainer role="search" aria-label="Event search" $mobileOpen={mobileOpen}>
      <MobileCloseSearchButton type="button" onClick={() => setMobileOpen(false)} aria-label="Close event search">
        ×
      </MobileCloseSearchButton>
      <SearchInputWrapper>
        <label htmlFor="event-search" className="visually-hidden">
          Search events
        </label>
        <input
          id="event-search"
          type="search"
          value={term}
          onChange={handleInputChange}
          placeholder="Search by title, tag, or location..."
          aria-label="Search events by title, tag, or location"
        />
        {term && (
          <ClearSearchButton type="button" onClick={handleClear} aria-label="Clear search">
            ×
          </ClearSearchButton>
        )}
      </SearchInputWrapper>
      {inlineControls && (
        <InlineControls>
          {inlineControls}
        </InlineControls>
      )}
      {!hideAddEventButton && (
        <AddEventButton
          to="/submit-event"
          aria-label="Submit a new show for review"
        >
          Add Show
        </AddEventButton>
      )}
    </SearchBarContainer>
  );
};

export default SearchBar;
